import { autoinject } from 'aurelia-framework';
import { json } from 'aurelia-fetch-client';
import { ApiService } from '../../utils/api-service';
import { AccountingRule, IAccountingRule, IAccountingRuleFolder, MissingMunicipalityAllocationRule, MissingServiceTargetingRule } from "./accounting-rule-models";

@autoinject
export class AccountingRuleApi {

	constructor(private api: ApiService) {}

	async addNewRule(ruleData: IAddRuleAM): Promise<Response> {
		return this.api.fetch(`accounting-rules`, {
			method: 'post',
			body: json(ruleData)
		});
	}

	async updateRule(rule: AccountingRule) : Promise<Response> {
		return this.api.fetch(`accounting-rules`, {
			method: 'put',
			body: json({
				id: rule.id,
				definition: rule.definition
			})
		});
	}

	async removeAccountingRule(rule: AccountingRule): Promise<Response> {
		return this.api.fetch(`accounting-rules/${rule.id}`, {
			method: 'delete'
		});
	}

	async updateOrderNumbers(rules: AccountingRule[]): Promise<Response> {

		const data = rules.map(rule => ({
			id: rule.id,
			orderNumber: rule.orderNumber
		}));

		return this.api.fetch(`accounting-rules/order-numbers`, {
			method: 'post',
			body: json(data)
		});
	}

	async updateRuleFolder(rule: AccountingRule): Promise<Response> {
		return this.api.fetch(`accounting-rules/${rule.id}/folder`, {
			method: 'patch',
			body: json({
				id: rule.id,
				folderId: rule.folderId
			})
		});
	}

	async createAccountingRuleFolderForScope(folder: IAddRuleFolderAM, scopeId: string): Promise<Response> {
		return this.api.fetch(`accounting-rules/folders/for-scope/${scopeId}`, {
			method: 'post',
			body: json(folder)
		});
	}

	async updateAccountingRuleFolderName(folderId: string, newName: string): Promise<Response> {
		return this.api.fetch(`accounting-rules/folders/${folderId}`, {
			method: 'patch',
			body: json(newName)
		});
	}

	async removeAccountingRuleFolder(folderId: string): Promise<Response> {
		return this.api.fetch(`accounting-rules/folders/${folderId}`, {
			method: 'delete'
		});
	}

	async getAccountingRulesForScope(scopeId: string): Promise<IAccountingRule[]> {
		return this.api.fetchJson(`accounting-rules/for-scope/${scopeId}`);
	}

	async getAccountingRuleFoldersForScope(scopeId: string): Promise<IAccountingRuleFolder[]> {
		return this.api.fetchJson(`accounting-rules/folders/for-scope/${scopeId}`);
	}

	async getMissingMunicipalityAllocationRules(scopeId: string): Promise<MissingMunicipalityAllocationRule[]> {
		const missingAllocations = await this.api.fetchJson(`accounting-rules/municipality-allocations-missing/for-scope/${scopeId}`);
		return missingAllocations.map(MissingMunicipalityAllocationRule.createNew);
	}

	async getMissingServiceTargetingRules(scopeId: string): Promise<MissingServiceTargetingRule[]> {
		const mappings = await this.api.fetchJson(`accounting-rules/service-targetings-missing/for-scope/${scopeId}`);
		return mappings.map(MissingServiceTargetingRule.createNew);
	}	
}

export interface IAddRuleAM {
	scopeId: string;
	typeName: string;
	definition: string;
	orderNumber: number;
	folderId?: string;
}

export interface IAddRuleFolderAM {
	scopeId: string;
	name: string;
	orderNumber: number;
}
